//--------------------------------------------------------------------------
// <copyright file="Userapp.js" company="Microsoft">
// Copyright (c) Microsoft Corporation. All rights reserved.
// </copyright>
//--------------------------------------------------------------------------
import React, { useState, useEffect } from "react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { webApiConfig, loginRequest, tokenRequest } from './utils/authConfig';
import './App.css';
import PairingInProgressDialog from "./components/PairingInProgressDialog";
import PairingCodeDialog from "./components/PairingCodeDialog";
import Footer from "./components/Footer";
import PairingStates from "./models/PairingStatesEnum";
import msalInstance from "./utils/msalInstance";
import { v4 as uuidv4 } from 'uuid';
import { Routes, Route } from 'react-router-dom';
import HardwareIDDialog from "./components/HardwareIDDialog";
import PairingAuthorizationFailedDialog from "./components/PairingAuthorizationFailedDialog";

/// <summary>
/// Helper methods and display components related to Userapp. Entry point to Userapp UI.
/// </summary>
const Userapp = () => {
  const [account, setAccount] = useState(null);
  const [username, setUsername] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pairingState, setPairingState] = useState(PairingStates.PairingCodeNotEntered);
  const [pairingCode, setPairingCode] = useState('');
  const [correlationId, setCorrelationId] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [errorMessages, setErrorMessages] = useState(null);
  const [hardwareId, setHardwareId] = useState('');
  const [authError, setAuthError] = useState(null);

  const logout = () => {
    const logoutRequest = {
      account: account,
    };

    msalInstance.logoutRedirect(logoutRequest);
  }

  const getAccounts = () => {
    const currentAccounts = msalInstance.getAllAccounts();

    if (currentAccounts.length === 0) {
      // no accounts signed-in, attempt to sign a user in
      msalInstance.loginRedirect(loginRequest);
    } else {
      // TODO: implement account chooser when currentAccounts.length > 1
      setAccount(msalInstance.getAccount({ username: currentAccounts[0].username }));
      setUsername(currentAccounts[0].username);
      setIsAuthenticated(true);
    }
  }

  const handleResponse = (response) => {
    if (response !== null && response.account) {
      setAccount(response.account);
      setUsername(response.account.username);
      setIsAuthenticated(true);
    } else {
      getAccounts();
    }
  }


  const signIn = () => {
    return msalInstance.handleRedirectPromise().then(handleResponse).catch(err => {
      console.error("Authentication error:", err);
      setAuthError(err);
    });
  }

  const acquireToken = (request) => {
    const requestWithAccount = { ...request, account };
    return msalInstance.acquireTokenSilent(requestWithAccount).catch(error => {
      if (error instanceof InteractionRequiredAuthError) {
        // fallback to interaction when silent call fails
        return msalInstance.acquireTokenRedirect(requestWithAccount);
      }
      throw error;
    });
  }

  useEffect(() => {
    if (pairingCode === '') {
      setSelectedOption(null);
      setPairingState(PairingStates.PairingCodeNotEntered);
    }

  }, [pairingCode])

  useEffect(() => {
    signIn().catch(() => {});
    if(correlationId === null)
    {
      var newCorrelationId = uuidv4();
      console.log(newCorrelationId);
      setCorrelationId(newCorrelationId);
    }
  }, []);

  if (isAuthenticated) {
    if (pairingState === PairingStates.PairingInProgress) {
      return (
        <div className={'overlay'}>
          <PairingInProgressDialog username={username} setPairingState={setPairingState} setPairingCode={setPairingCode} pairingCode={pairingCode} logout={logout}  ></PairingInProgressDialog>
          <Footer />
        </div>

      );
    }
    else if (pairingState === PairingStates.PairingAuthorizationFailed) {
      return (
        <div className={'overlay'}>
          <PairingAuthorizationFailedDialog username={username} setPairingState={setPairingState} setHardwareId={setHardwareId} hardwareId={hardwareId} logout={logout} errorMessages={errorMessages} setSelectedOption={setSelectedOption} ></PairingAuthorizationFailedDialog>
          <Footer />
        </div>

      );
    }
    else {
      return (
        <div className={'overlay'}>
          <Routes>
          <Route path="/" element={<PairingCodeDialog username={username} setPairingState={setPairingState} pairingState={pairingState} setPairingCode={setPairingCode} pairingCode={pairingCode} logout={logout} acquireToken={acquireToken} correlationId={correlationId} ></PairingCodeDialog>} />
          <Route path="/hardwareidlogin" element={<HardwareIDDialog username={username} setPairingState={setPairingState} pairingState={pairingState} setHardwareId={setHardwareId} hardwareId={hardwareId} logout={logout} acquireToken={acquireToken} correlationId={correlationId} selectedOption={selectedOption} setSelectedOption={setSelectedOption} errorMessages={errorMessages} setErrorMessages={setErrorMessages} ></HardwareIDDialog>} />
          </Routes>
          <Footer />
        </div>

      );
    }
  }

  if (authError) {
    return (
      <div className={'overlay'}>
        <div>Authentication failed. Please refresh the page.</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className={'overlay'}>
      Signing in...
      <Footer />
    </div>
  );
}

export default Userapp;
